/**
 * Géocodage via l'API Nominatim (OpenStreetMap).
 * Gratuit, pas de clé API, limite : 1 requête/seconde.
 */

const NOMINATIM_URL = 'https://nominatim.openstreetmap.org';
const DEBOUNCE_MS = 400;

let debounceTimer = null;

/**
 * Recherche d'adresses (forward geocoding) via Nominatim.
 * @param {string} query - Texte de recherche
 * @param {{ lat?: number, lng?: number }} options - Position pour biaiser les résultats
 * @returns {Promise<Array<{ display_name: string, lat: number, lng: number }>>}
 */
export async function searchAddress(query, { lat, lng } = {}) {
  const trimmed = query?.trim();
  if (!trimmed || trimmed.length < 3) return [];

  const params = new URLSearchParams({
    q: trimmed,
    format: 'json',
    addressdetails: '1',
    limit: '5',
    countrycodes: 'fr',
    'accept-language': 'fr',
  });

  if (lat && lng) {
    params.set('viewbox', `${lng - 0.5},${lat + 0.5},${lng + 0.5},${lat - 0.5}`);
    params.set('bounded', '0');
  }

  const res = await fetch(`${NOMINATIM_URL}/search?${params}`, {
    headers: { 'User-Agent': 'GroceWise/1.0' },
  });

  if (!res.ok) return [];

  const data = await res.json();

  return data.map((item) => ({
    display_name: item.display_name,
    lat: parseFloat(item.lat),
    lng: parseFloat(item.lon),
  }));
}

/**
 * Recherche d'adresses avec debounce.
 * @param {string} query
 * @param {object} options
 * @returns {Promise<Array>}
 */
export function searchAddressDebounced(query, options) {
  return new Promise((resolve) => {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(async () => {
      try {
        const results = await searchAddress(query, options);
        resolve(results);
      } catch {
        resolve([]);
      }
    }, DEBOUNCE_MS);
  });
}

/**
 * Reverse geocoding : coordonnées GPS → adresse lisible.
 * @param {number} lat
 * @param {number} lng
 * @returns {Promise<string|null>} Adresse formatée ou null en cas d'erreur
 */
export async function reverseGeocode(lat, lng) {
  const params = new URLSearchParams({
    lat: String(lat),
    lon: String(lng),
    format: 'json',
    'accept-language': 'fr',
    zoom: '18',
  });

  try {
    const res = await fetch(`${NOMINATIM_URL}/reverse?${params}`, {
      headers: { 'User-Agent': 'GroceWise/1.0' },
    });

    if (!res.ok) return null;

    const data = await res.json();
    if (!data.display_name) return null;

    // Raccourcir l'adresse : garder les éléments importants
    const addr = data.address || {};
    const parts = [
      addr.road || addr.pedestrian || addr.footway || '',
      addr.house_number || '',
    ].filter(Boolean);

    const street = parts.join(' ').trim();
    const city = addr.city || addr.town || addr.village || addr.municipality || '';
    const postcode = addr.postcode || '';

    if (street && city) {
      return `${street}, ${postcode ? postcode + ' ' : ''}${city}`;
    }

    return data.display_name.split(',').slice(0, 3).join(',').trim();
  } catch {
    return null;
  }
}
